import type { SDKState } from "@/state/sdk-state";
import { SimpleObservable, SimpleObservableSubject } from "@/utils/observable";
import * as z from "zod/v4-mini";
import type { Logger } from "@/utils/logger";
import { EmbedMessageType } from "../embed/embed-broker";

const asyncFlowMessageEventName = "fanfest:async-flow-message";

export enum AsyncFlowMessageType {
  ValidateAuthenticationSuccess = "validate-authentication:success",
  ValidateAuthenticationError = "validate-authentication:error",
}

const validateAuthenticationSuccessSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationSuccess),
  code: z.string(),
  state: z.string(),
  redirect_uri: z.string(),
});

const validateAuthenticationErrorSchema = z.object({
  type: z.literal(AsyncFlowMessageType.ValidateAuthenticationError),
  error: z.string(),
});

const asyncFlowMessageSchema = z.object({
  type: z.literal(asyncFlowMessageEventName),
  data: z.discriminatedUnion("type", [
    validateAuthenticationSuccessSchema,
    validateAuthenticationErrorSchema,
  ]),
});

export type AuthIframeManagerAuthEvent = z.infer<typeof asyncFlowMessageSchema>;

export class AuthIframeBroker extends SimpleObservable {
  #state: SDKState;
  #logger: Logger;
  authObservable: SimpleObservableSubject<AuthIframeManagerAuthEvent>;

  constructor(
    state: SDKState,
    logger: Logger,
    authObservable: SimpleObservableSubject<AuthIframeManagerAuthEvent> = new SimpleObservableSubject<AuthIframeManagerAuthEvent>(),
  ) {
    super();
    this.#state = state;
    this.authObservable = authObservable;
    this.#logger = logger.extend(this.constructor.name);
  }

  handleMessage = (event: MessageEvent) => {
    if (event.origin !== this.#state.options.appOrigin) return;
    if (event.data.type !== asyncFlowMessageEventName) return;

    const result = asyncFlowMessageSchema.safeParse(event.data);

    if (result.success) {
      this.notify(this.authObservable, result.data);
    } else {
      this.#logger.error(
        "Received message with invalid data",
        result.error,
        event,
      );
    }
  };

  init() {
    window.addEventListener("message", this.handleMessage);
  }

  detach() {
    window.removeEventListener("message", this.handleMessage);
    this.authObservable.clear();
  }

  sendMessage(message: AuthIframeManagerAuthEvent) {
    window.postMessage(message, this.#state.options.appOrigin);
  }
}
